import React, { useEffect, useState } from "react";
import { useFloating, offset, flip, shift, autoUpdate, useClick, useDismiss, useRole, useInteractions } from "@floating-ui/react";
import { Icons } from "../../design_system/components/Icons";
import ReactDOM from "react-dom";
import { profileCache } from "../utils/CacheUtils";

/**
 * @param {React.Component} DesignComponent - The component to render inside the card
 * @param {Object} options - Configuration options
 * @param {string} options.portalId - Portal container ID (default: "new-directory")
 * @param {string} options.scrollAreaId - Scroll area element ID to manage overflow (optional)
 * @param {Object} options.cardStyle - Additional card container styles
 */
export const AnProfileCardWrapper = ({ 
    DesignComponent, 
    portalId = "new-directory",
    scrollAreaId = null,
    cardStyle = {},
    triggerIcon = { open: "windfall", closed: "windfall" },
    renderTrigger = null,
    placement = "right",
    ...props 
}) => {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const clearCache = () => profileCache.clear();
        window.addEventListener('beforeunload', clearCache);
        return () => window.removeEventListener('beforeunload', clearCache);
    }, []);
    useEffect(() => {
        if (!scrollAreaId) return;
        
        const tableElement = document.getElementById(scrollAreaId);
        if (!tableElement) return;
        
        tableElement.style.overflow = "auto";
        tableElement.style.height = "100%";
        tableElement.style.paddingRight = "0px";
        
        if (open) {
            tableElement.style.overflow = "hidden";
            tableElement.style.paddingRight = "8px";
        }
        
        return () => {
            tableElement.style.overflow = "auto";
            tableElement.style.paddingRight = "0px";
        };
    }, [open, scrollAreaId]);

    const {
        refs,
        floatingStyles,
        context,
    } = useFloating({
        open,
        onOpenChange: setOpen,
        placement: placement,
        middleware: [
            offset(8),
            flip(),
            shift({ padding: 8 })
        ],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "dialog" });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    const defaultCardStyle = {
        position: 'absolute',
        left: 0,
        background: "var(--color-surface-page)",
        boxShadow: "var(--shadow-l3)",
        borderRadius: "var(--radius-xs)",
        zIndex: "var(--z-dropdown)",
        maxHeight: 280,
        width: 512,
        display: 'flex',
        flexDirection: 'column',
        ...floatingStyles,
        ...cardStyle,
    };

    const renderCard = () => (
        <div
            ref={refs.setFloating}
            style={defaultCardStyle}
            tabIndex={-1}
            {...getFloatingProps()}
        >
            <DesignComponent 
                {...props} 
                open={open} 
                setOpen={setOpen}
            />
        </div>
    );

    const portalContainer = document.getElementById(portalId);

    return (
        <div style={{ position: 'relative' }}>
            <div
                ref={refs.setReference}
                style={{ cursor: 'pointer' }}
                {...getReferenceProps()}
            >
                {renderTrigger ? renderTrigger({ open }) : <Icons name={open ? triggerIcon.open : triggerIcon.closed} />}
            </div>
            {open && portalContainer && ReactDOM.createPortal(renderCard(), portalContainer)}
        </div>
    );
};

export default AnProfileCardWrapper;
